import * as React from 'react';
import { compose } from 'redux';
import { withProps } from 'recompose';
import last from 'lodash/last';
import * as ENUM from '@dpdgroupuk/redback-enums';
import { useOverlay } from '@dpdgroupuk/fmx-ui/components/Overlay';
import { useGoogleReCaptcha } from '@dpdgroupuk/react-google-recaptcha-v3';
import { useModal } from '@dpdgroupuk/fmx-ui/components/Dialog';
import { withTrackProps } from '@dpdgroupuk/react-event-tracker';

import { noop } from '../../../../utils/function';
import { CFN_STEPS } from '../../steps';
import type { AuthorizedCollection } from '../../../../types/Collection';
import { uploadCollectionPhoto } from '../../../../apis';
import ChangeDate from '../../components/ChangeDate';
import { hasCollectionNotificationDetails } from '../../../../models/collection';
import DropZone from '../../../../components/DropZone';
import {
  CFN_DND_TEXT,
  CLOSE,
  PROBLEM_COMPLETING_REQUEST,
} from '../../../../constants/message';
import ChangeNeighbour from '../../components/ChangeNeighbour';
import { ACTION } from '../../../../constants/recaptcha';
import { WIZARD_COMMON } from '../../../../constants/analytics';

type Props = {
  data: Object,
  onSubmit: Function,
  onSkip: Function,
  onReplacePhoto: Function,
  onDateChange: Function,
  onNeighbourChange: Function,
  addStepData: Function,
  collection: AuthorizedCollection,
  dates: Array<string>,
};

const UploadPhotoStep = ({
  data,
  dates,
  onSubmit,
  onSkip,
  addStepData,
  onReplacePhoto,
  onDateChange,
  onNeighbourChange,
  collection,
}: Props) => {
  const { showWhile } = useOverlay();
  const { showModal } = useModal();
  const { executeRecaptchaAsync } = useGoogleReCaptcha();
  const { photoUrl } = data;

  const uploadPhoto = React.useCallback(
    showWhile(async acceptedFiles => {
      try {
        const token = await executeRecaptchaAsync(ACTION.UPLOAD_CFN_PHOTO);
        const { imageKey, photoUrl } = await uploadCollectionPhoto(
          last(acceptedFiles),
          collection.collectionCode,
          ENUM.COLLECTION_ACTION_CODE.CFN,
          token
        );
        addStepData({
          imageKey,
          photoUrl,
        });
      } catch (error) {
        showModal({
          title: PROBLEM_COMPLETING_REQUEST,
          description: null,
          buttonText: CLOSE,
        });
      }
    }),
    [addStepData, executeRecaptchaAsync, uploadCollectionPhoto]
  );

  return (
    <>
      <ChangeDate
        addStepData={addStepData}
        day={dates && dates[0]}
        dates={dates}
        collection={collection}
        onDateChange={onDateChange}
      />
      <ChangeNeighbour data={data} onNeighbourChange={onNeighbourChange} />
      <DropZone
        description={CFN_DND_TEXT}
        onSubmit={onSubmit}
        onSkip={onSkip}
        onReplacePhoto={onReplacePhoto}
        onDropAccepted={uploadPhoto}
        photoUrl={photoUrl}
      />
    </>
  );
};

UploadPhotoStep.defaultProps = {
  data: { day: '' },
  addStepData: noop,
};

export default compose(
  withProps(({ collection, submitWizard, addStepData, goToStep }) => {
    const onSubmit = hasCollectionNotificationDetails(collection)
      ? submitWizard
      : () => goToStep(CFN_STEPS.GET_CONTACTS);

    return {
      onSubmit,
      onSkip: () => {
        addStepData({
          photoUrl: '',
        });
        onSubmit();
      },
      onReplacePhoto: () =>
        addStepData({
          photoUrl: '',
          imageKey: '',
        }),
      onDateChange: () => goToStep(CFN_STEPS.SELECT_DAY),
      onNeighbourChange: () => goToStep(CFN_STEPS.SELECT_NEIGHBOUR),
    };
  }),
  withTrackProps({
    onDateChange: WIZARD_COMMON.ON_DATE_CHANGE,
  })
)(UploadPhotoStep);
